// Shared WebGL plumbing for the voucher card canvases, the morph and the shader warm-up.

type GL = WebGLRenderingContext | WebGL2RenderingContext;

const compile = (gl: GL, type: number, src: string) => {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (gl.getShaderParameter(s, gl.COMPILE_STATUS)) return s;
  gl.deleteShader(s);
  return null;
};

/** Compiles and links `vs` + `fs` with attribute `a` at location 0; null on any failure. */
export const createGlProgram = (
  gl: GL,
  vs: string,
  fs: string,
): WebGLProgram | null => {
  const v = compile(gl, gl.VERTEX_SHADER, vs);
  const f = v && compile(gl, gl.FRAGMENT_SHADER, fs);
  const prog = f && gl.createProgram();
  if (!v || !f || !prog) {
    if (v) gl.deleteShader(v);
    if (f) gl.deleteShader(f);
    return null;
  }
  gl.attachShader(prog, v);
  gl.attachShader(prog, f);
  gl.bindAttribLocation(prog, 0, 'a');
  gl.linkProgram(prog);
  gl.deleteShader(v);
  gl.deleteShader(f);
  if (gl.getProgramParameter(prog, gl.LINK_STATUS)) return prog;
  gl.deleteProgram(prog);
  return null;
};

/**
 * Program in use with a viewport-covering triangle on attribute `a`; draw it
 * with `drawFullScreen`. Null on any failure.
 */
export const createFullScreenProgram = (
  gl: GL,
  vs: string,
  fs: string,
): WebGLProgram | null => {
  const prog = createGlProgram(gl, vs, fs);
  if (!prog) return null;
  gl.useProgram(prog);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 3, -1, -1, 3]),
    gl.STATIC_DRAW,
  );
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  return prog;
};

export const drawFullScreen = (gl: GL) => gl.drawArrays(gl.TRIANGLES, 0, 3);

/** Releases the context now instead of waiting for GC (browsers cap live contexts). */
export const loseGl = (gl: GL | null | undefined) =>
  gl?.getExtension('WEBGL_lose_context')?.loseContext();
